@file:Suppress("CssInvalidHtmlTagReference")

package streetlight.web.pages

import koala.PageResource
import koala.modifier.*
import koala.html.*
import kotlinx.html.DIV
import kotlinx.html.HTML
import kotlinx.html.body
import streetlight.model.data.PageTheme

/**
 * The body of an app page: the site menu, the content panel with the portal mount and the server-rendered shell
 * of [shellBlock], the star panel, and the overlay. The page's [theme] is applied to the body.
 */
fun HTML.appBody(
    screen: AppScreen,
    resource: PageResource,
    theme: PageTheme? = null,
    shellBlock: (DIV.() -> Unit)? = null
) {
    body {
        theme?.let {
            applyTheme(theme)
        }
        setAttribute(KoalaBody.ScreenId.to(screen.screenId))
        div(AppBody.Viewport) {
            div(AppBody.PanelGrid) {
                box(AppBody.LeftPanel) {
                    siteMenuSidebar()
                }
                box(AppBody.ContentPanel) {
                    div(KoalaBody.PortalMount)
                    shellBlock?.let {
                        div(KoalaBody.ShellMount, block = it)
                    }
                }
                div(AppBody.RightPanel) {
                    div(StarHelm.StarPanelHelm)
                }
            }
        }
        div(AppBody.FullScreen)
        appOverlay()
        div(AppBody.ToasterId)

        scriptUnsafe(AppOverlayScript)
        linkScript(resource.bundle.web)
    }
}

object AppBody {
    val Viewport = Id("app-viewport")
    val PanelGrid = Id("panel-grid")
    val ContentPanel = Id("content-panel")
    val LeftPanel = Id("left-panel")
    val RightPanel = Id("right-panel")
    val FullScreen = Id("full-screen")
    val ToasterId = Id("toaster")
}

// language="CSS"
val AppBodyCss get() = with(AppBody) { """
$Viewport {
    width: 100%;
    isolation: isolate;
}

$PanelGrid {
    display: grid;
    min-height: 100dvh;
    gap: var(--unit-1);
    grid-template-columns: minmax(auto, 1fr) minmax(0, var(--content-panel-width)) minmax(auto, 1fr);
    /* grid-template-columns: auto minmax(0, var(--content-panel-width)) auto; */
    grid-template-areas: "left content right";
    justify-content: center;
}

$ContentPanel {
    grid-area: content;
}

$LeftPanel,
$RightPanel {
    display: none;
    position: sticky;
    place-items: center;
    top: calc(var(--unit) * 8);
    height: calc(100lvh - var(--unit) * 16);
    /* min-width: calc((100vw - ${CONTENT_PANEL_WIDTH_PX}px) / 2); */
    min-width: ${SIDE_PANEL_WIDTH_PX}px;
    
    @media (min-width: ${CONTENT_PANEL_WIDTH_PX + SIDE_PANEL_WIDTH_PX * 2}px) {
        display: grid;
    }
}

:root {
    &${AppOverlay.RevealRightPanel} $RightPanel,
    &${AppOverlay.RevealLeftPanel} $LeftPanel {
        display: grid;
    }
}

$LeftPanel {
    grid-area: left;
}

$RightPanel {
    grid-area: right;
    /* padding-right: var(--unit-1); */
    /* width: var(--right-panel-width); */
}

$LeftPanel {
    /* padding-left: var(--unit-1); */
}

${KoalaBody.PortalMount},
${KoalaBody.ShellMount} {
    display: flex;
    flex-direction: column;
    grid-area: 1 / 1;
    min-width: 0;
}

${AppBody.FullScreen} {
    position: fixed;
    inset: 0;
    pointer-events: none;
    opacity: 0;
    transition: var(--transition-opacity);
}

${AppBody.FullScreen}$Reveal {
    pointer-events: auto;
    opacity: 1;
}

${AppBody.ToasterId} {
    position: fixed;
    inset: 0;
    pointer-events: none;
    display: flex;
    flex-direction: column;
    justify-content: end;
}

""" }

const val RIGHT_PANEL_KEY = "streetlight.right-panel"